// client#2: more than one service item (agreement line) on a shift at creation.
// The shift's default segment (first item) is shortened to its own window and every extra item becomes its own
// segment — the same two calls the Plan tab "Split" makes. Pure helpers, overnight-aware (window relative to shift start).

const toMin = (t) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t || '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const fromMin = (m) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** Minutes from shift start; the shift end maps to the full length (so 22:00–06:00 is 0..480). */
function rel(shiftStart, shiftEnd, t) {
  const s = toMin(shiftStart); const e = toMin(shiftEnd); const x = toMin(t);
  if (s == null || e == null || x == null) return null;
  const len = ((e - s + 1440) % 1440) || 1440;
  const r = (x - s + 1440) % 1440;
  return r === 0 && x === e && len === 1440 ? len : r;
}

export function shiftLength(start, end) {
  const s = toMin(start); const e = toMin(end);
  if (s == null || e == null) return 0;
  return ((e - s + 1440) % 1440) || 1440;
}

/** Window [from, to] relative to the shift, or null when a time is missing / invalid. */
function windowOf(shift, item) {
  const a = rel(shift.start_time, shift.end_time, item.start);
  let b = rel(shift.start_time, shift.end_time, item.end);
  if (a == null || b == null) return null;
  if (b === 0) b = shiftLength(shift.start_time, shift.end_time);   // ends exactly at shift end
  return [a, b];
}

/** Split the last item in half (rounded to 15 min) to make room for a new one — like the Plan tab Split. */
export function addItem(shift, items) {
  const last = items[items.length - 1];
  const w = windowOf(shift, last) || [0, shiftLength(shift.start_time, shift.end_time)];
  const mid = w[0] + Math.max(15, Math.round((w[1] - w[0]) / 2 / 15) * 15);
  const midT = fromMin((toMin(shift.start_time) + Math.min(mid, w[1])) % 1440);
  const shortened = items.map((it, i) => (i === items.length - 1 ? { ...it, end: midT } : it));
  return [...shortened, { _key: `x${Date.now()}${Math.random().toString(36).slice(2, 6)}`, psi: '', start: midT, end: last.end }];
}

/** First problem with the items, in words, or null. items[0] is the main (default) service item. */
export function validateItems(shift, items) {
  if (!items || items.length < 2) return null;
  const len = shiftLength(shift.start_time, shift.end_time);
  const wins = [];
  for (let i = 0; i < items.length; i += 1) {
    const it = items[i];
    const label = i === 0 ? 'The main service item' : `Service item ${i + 1}`;
    if (!it.psi) return `${label}: choose the authorised service`;
    const w = windowOf(shift, it);
    if (!w) return `${label}: enter a start and end time`;
    if (w[1] <= w[0]) return `${label}: the end time must be after the start time`;
    if (w[0] < 0 || w[1] > len) return `${label}: the time must be inside the shift (${shift.start_time}–${shift.end_time})`;
    wins.push({ i, w, label });
  }
  const sorted = [...wins].sort((x, y) => x.w[0] - y.w[0]);
  for (let k = 1; k < sorted.length; k += 1) {
    if (sorted[k].w[0] < sorted[k - 1].w[1]) return `${sorted[k - 1].label} and ${sorted[k].label.toLowerCase()} overlap`;
  }
  return null;
}

/** No service agreement: the shift has no default segment, so EVERY item (main included) becomes a catalogue line-item
 *  segment billed from the participant's funding plan (the API resolves the plan). Returns error messages (empty = ok). */
export async function saveCatalogueItems(api, shiftId, participantId, items) {
  const errors = [];
  if (!items || items.length < 2) return errors;
  const msg = (e) => e?.response?.data?.detail?.message || e?.response?.data?.detail?.error?.message || e?.response?.data?.detail || 'failed';
  for (let i = 0; i < items.length; i += 1) {
    const it = items[i];
    try {
      await api.post(`/shifts/${shiftId}/service-allocations`, {
        participant_id: participantId,
        service_catalog_id: it.psi,
        allocation_start_time: it.start,
        allocation_end_time: it.end,
        allocation_order: i + 1,
        is_primary: i === 0,
      });
    } catch (e) {
      errors.push(`${i === 0 ? 'Main line item' : `Service item ${i + 1}`}: ${msg(e)}`);
    }
  }
  return errors;
}

/** Save: shorten the default segment to items[0], then add items[1..] as segments. Returns error messages (empty = ok). */
export async function saveExtraItems(api, shiftId, participantId, items, optionFor) {
  const errors = [];
  if (!items || items.length < 2) return errors;
  const msg = (e) => e?.response?.data?.detail?.message || e?.response?.data?.detail?.error?.message || e?.response?.data?.detail || 'failed';
  try {
    const r = await api.get(`/shifts/${shiftId}/service-allocations`);
    const rows = (r.data?.data || r.data?.items || []).filter(x => x.status !== 'DELETED');
    const primary = rows.find(x => x.is_primary) || rows[0];
    if (!primary) return ['The default service item was not found — add the other items in the Plan tab'];
    await api.put(`/shifts/${shiftId}/service-allocations/${primary.id}`, {
      allocation_start_time: items[0].start, allocation_end_time: items[0].end, is_primary: true, version: primary.version,
    });
  } catch (e) {
    return [`Main service item time: ${msg(e)}`];
  }
  for (let i = 1; i < items.length; i += 1) {
    const it = items[i];
    const o = optionFor(it.psi) || {};
    try {
      await api.post(`/shifts/${shiftId}/service-allocations`, {
        participant_id: participantId,
        participant_service_item_id: it.psi,
        agreement_line_id: o.agreement_line_id || undefined,
        allocation_start_time: it.start,
        allocation_end_time: it.end,
        is_primary: false,
      });
    } catch (e) {
      errors.push(`Service item ${i + 1}: ${msg(e)}`);
    }
  }
  return errors;
}
